import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layer, Line, Stage, Text, Circle, Group } from "react-konva";
import { StickyNote } from "../setting/StickyNote";
import useUserMode from "../store/useUserMode";

const ST_SELECT = "select";
const ST_RECTANGLE = "callout";
const ST_LINE = "line";
const ST_VERTICAL_LINE = "vertical";
const ST_HORIZONTAL_LINE = "horizontal";
const ST_FIBONACCI = "fibonacci";
const ST_UNDO = "undo";
const ST_CLEAR = "clear";

function useStateCallback(initialState) {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null);

  const setStateCallback = useCallback((state, cb) => {
    cbRef.current = cb;
    setState(state);
  }, []);

  useEffect(() => {
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null;
    }
  }, [state]);

  return [state, setStateCallback];
}

const AnnotationStage = ({ setting, height, width, data, style }) => {
  const stageRef = useRef(null);
  const isDrawing = useRef(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const [shapes, setShapes] = useStateCallback([]);
  const history = useRef([]);
  const [fibonacciLines, setFibonacciLines] = useState([]);
  const userMode = useUserMode((state) => state.userMode);
  const [texts, setTexts] = useState({});
  const [swidth, setSwidth] = useState(200);
  const [sheight, setSheight] = useState(200);
  const [selected, setSelected] = useState(false);
  const [disableScroll, setDisableScroll] = useState(false);

  // For whole line dragging: store initial drag info per shape index.
  const groupDragInfo = useRef({});

  useEffect(() => {
    const handleTouchMove = (event) => {
      if (disableScroll) {
        event.preventDefault();
      }
    };

    if (disableScroll) {
      document.body.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    } else {
      document.body.removeEventListener("touchmove", handleTouchMove);
    }

    return () => {
      document.body.removeEventListener("touchmove", handleTouchMove);
    };
  }, [disableScroll]);

  useEffect(() => {
    if (history.current.length > 0) {
      history.current.pop();
      setShapes(history.current[history.current.length - 1] || []);
    }
  }, []);


useEffect(() => {
  isDrawing.current = false;  // Cancel active drawing when switching mode
  // your switch code here...
}, [userMode]);


  useEffect(() => {
    if (isDrawing.current) {
  isDrawing.current = false;
  setShapes(prev => prev.slice(0, -1));  // remove last
}


    switch (userMode) {
      case ST_SELECT:
        setIsDraggable(true);
        break;
      case ST_LINE:
        setIsDraggable(false);
        break;
      case ST_RECTANGLE:
        setIsDraggable(false);
        break;
      case ST_FIBONACCI: {
        const yMaxValues = data.map((d) => d.bb.top);
        const yMinValues = data.map((d) => d.bb.bottom);
        const yMaxAxisValues = data.map((d) => d.bb.top);
        const yMinAxisValues = data.map((d) => d.bb.bottom);

        let maxPoint = Math.max(...yMaxValues);
        let minPoint = Math.min(...yMinValues);

        const yMaxAxis = Math.max(...yMaxAxisValues);
        const yMinAxis = Math.min(...yMinAxisValues);
        maxPoint =
          (window.innerHeight * (maxPoint - yMinAxis)) / (yMaxAxis - yMinAxis);
        minPoint =
          (window.innerHeight * (minPoint - yMinAxis)) / (yMaxAxis - yMinAxis);

        const diff = maxPoint - minPoint;

        setFibonacciLines([
          { pos: minPoint, ratio: "0.0" },
          { pos: minPoint + diff * 0.236, ratio: "23.6" },
          { pos: minPoint + diff * 0.382, ratio: "38.2" },
          { pos: minPoint + diff * 0.5, ratio: "50.0" },
          { pos: minPoint + diff * 0.618, ratio: "61.8" },
          { pos: maxPoint, ratio: "100.0" },
        ]);

        const newShapes = [
          ...shapes,
          { type: ST_FIBONACCI, points: [fibonacciLines] },
        ];

        setShapes(newShapes);
        history.current = [...history.current, newShapes];
        break;
      }
      case ST_UNDO:
        if (history.current.length > 0) {
          history.current.pop();
          setShapes(history.current[history.current.length - 1] || []);
        }
        break;
      case ST_CLEAR:
        setShapes([]);
        break;
      default:
        break;
    }
  }, [userMode]);

  function resetSelection() {
    const myCustomEvent = new CustomEvent("resetSelection", {
      detail: { resetSelection: true },
    });
    window.dispatchEvent(myCustomEvent);
  }

  const handleMouseDown = (e) => {
    if (!setting.drawMode) return;
    isDrawing.current = true;

  // in select mode, don't interfere — let Konva handle dragging
  if (userMode === ST_SELECT) return;

  isDrawing.current = true;
  const pos = e.target.getStage().getPointerPosition();

    switch (userMode) {
      case ST_RECTANGLE:
        setShapes(
          [...shapes, { type: ST_RECTANGLE, points: [pos.x, pos.y, 0, 0] }],
          () => {
            resetSelection();
          }
        );
        break;
      case ST_LINE:
        setDisableScroll(true);
        setShapes([...shapes, { type: ST_LINE, points: [pos.x, pos.y, pos.x, pos.y] }]);
        break;
      case ST_VERTICAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          { type: ST_VERTICAL_LINE, points: [pos.x, 0, pos.x, window.innerHeight] },
        ]);
        break;
      case ST_HORIZONTAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          { type: ST_HORIZONTAL_LINE, points: [0, pos.y, window.innerWidth, pos.y] },
        ]);
        break;
      default:
        break;
    }
  };

const handleMouseMove = (e) => {
  if (!isDraggable && shapes.length > 0 && isDrawing.current) {
    const pos = e.target.getStage().getPointerPosition();
    let newShape = { ...shapes[shapes.length - 1] };

    // 🛡️ Safety: Only update if the shape matches the current mode!
    if (newShape.type !== userMode) return;

    switch (newShape.type) {
      case ST_RECTANGLE:
        newShape.points[2] = pos.x - newShape.points[0];
        newShape.points[3] = pos.y - newShape.points[1];
        break;
      case ST_LINE:
        setDisableScroll(true);
        newShape.points[2] = pos.x;
        newShape.points[3] = pos.y;
        break;
      default:
        break;
    }
    setShapes([...shapes.slice(0, shapes.length - 1), newShape]);
  }
};


  const handleMouseUp = () => {
    if (!isDraggable) {
      isDrawing.current = false;
      history.current = [...history.current, shapes];
      setDisableScroll(false);
    }
  };

  const handleTouchStart = (e) => {
    if (!setting.drawMode) return;
    isDrawing.current = true;
    const touchPos = e.target.getStage().getPointerPosition();
    const pos = { x: touchPos.x, y: touchPos.y };

    switch (userMode) {
      case ST_RECTANGLE:
        setShapes(
          [...shapes, { type: ST_RECTANGLE, points: [pos.x, pos.y, 0, 0] }],
          () => {
            resetSelection();
          }
        );
        break;
      case ST_LINE:
        setDisableScroll(true);
        setShapes([...shapes, { type: ST_LINE, points: [pos.x, pos.y, pos.x, pos.y] }]);
        break;
      case ST_VERTICAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          { type: ST_VERTICAL_LINE, points: [pos.x, 0, pos.x, window.innerHeight] },
        ]);
        break;
      case ST_HORIZONTAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          { type: ST_HORIZONTAL_LINE, points: [0, pos.y, window.innerWidth, pos.y] },
        ]);
        break;
      default:
        break;
    }
  };

  const handleTouchMove = (e) => {
    if (isDrawing && shapes.length > 0 && isDrawing.current) {
      const touchPos = e.target.getStage().getPointerPosition();
      const pos = { x: touchPos.x, y: touchPos.y };
      let newShape = { ...shapes[shapes.length - 1] };
      newShape.points = newShape.points.slice();

      switch (newShape.type) {
        case ST_RECTANGLE:
          newShape.points[2] = pos.x - newShape.points[0];
          newShape.points[3] = pos.y - newShape.points[1];
          break;
        case ST_LINE:
          setDisableScroll(true);
          newShape.points[2] = pos.x;
          newShape.points[3] = pos.y;
          break;
        default:
          break;
      }

      setShapes([...shapes.slice(0, shapes.length - 1), newShape]);
    }
  };

  const handleTouchEnd = () => {
    isDrawing.current = false;
    history.current = [...history.current, shapes];
    setDisableScroll(false);
  };

  const handleTextChange = (index, value) => {
    const updatedTexts = { ...texts };
    updatedTexts[index] = value;
    setTexts(updatedTexts);
  };

  return (
    <>
      <Stage
        style={style}
        width={width - 40}
        height={height - 25}
        ref={stageRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
      >
        <Layer>
          {shapes.map((shape, i) => {
            if (shape.type === ST_RECTANGLE) {
              return (
            <StickyNote
              key={i}
              x={shape.points[0]}
              y={shape.points[1]}
              text={texts[i] || ""}
              colour="#FFDAE1"
              onTextChange={(value) => handleTextChange(i, value)}
              width={220}
              height={50}
              selected={selected}
              draggable={userMode === ST_SELECT}  // <---- key point
              onTextResize={(newWidth, newHeight) => {
                setSwidth(newWidth);
                setSheight(newHeight);
              }}
              onClick={() => setSelected(!selected)}
              onTextClick={(newSelected) => setSelected(newSelected)}
            />

              );
            } else if (shape.type === ST_FIBONACCI) {
              return (
                <React.Fragment key={i}>
                  {fibonacciLines.map((line, idx) => (
                    <React.Fragment key={idx}>
                      <Line
                        points={[0, line.pos, window.innerWidth, line.pos]}
                        stroke="red"
                        strokeWidth={1}
                        draggable={isDraggable}
                      />
                      <Text text={`${line.ratio}%`} y={line.pos} fill="red" />
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            } else if (shape.type === ST_LINE) {
              return (
                <Group key={i}>
                  {/* Visual line */}
                  <Line
                    points={shape.points}
                    stroke="black"
                    strokeWidth={1}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={
                      isDraggable ? "source-over" : "destination-over"
                    }
                    // Line itself is not directly draggable.
                    draggable={false}
                  />
                  {/* Draggable transparent overlay to move the whole line */}
                  <Line
                    points={shape.points}
                    stroke="black"
                    strokeWidth={10}
                    opacity={0}
                    draggable={isDraggable}
                    onDragStart={(e) => {
                      groupDragInfo.current[i] = {
                        initialPoints: shape.points.slice(),
                        initialPos: { x: e.target.x(), y: e.target.y() },
                      };
                    }}
                    onDragMove={(e) => {
                      const info = groupDragInfo.current[i];
                      if (info) {
                        const deltaX = e.target.x() - info.initialPos.x;
                        const deltaY = e.target.y() - info.initialPos.y;
                        const newPoints = [
                          info.initialPoints[0] + deltaX,
                          info.initialPoints[1] + deltaY,
                          info.initialPoints[2] + deltaX,
                          info.initialPoints[3] + deltaY,
                        ];
                        const newShapes = [...shapes];
                        newShapes[i] = { ...newShapes[i], points: newPoints };
                        setShapes(newShapes);
                      }
                    }}
                    onDragEnd={(e) => {
                      // Reset overlay's position after dragging.
                      e.target.position({ x: 0, y: 0 });
                    }}
                  />
                  {/* Start endpoint (smaller circle) */}
                  <Circle
                    x={shape.points[0]}
                    y={shape.points[1]}
                    radius={3}
                    fill="black"
                    draggable={isDraggable}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newShapes = [...shapes];
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          pos.x,
                          pos.y,
                          newShapes[i].points[2],
                          newShapes[i].points[3],
                        ],
                      };
                      setShapes(newShapes);
                    }}
                  />
                  {/* End endpoint (smaller circle) */}
                  <Circle
                    x={shape.points[2]}
                    y={shape.points[3]}
                    radius={3}
                    fill="black"
                    draggable={isDraggable}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newShapes = [...shapes];
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          newShapes[i].points[0],
                          newShapes[i].points[1],
                          pos.x,
                          pos.y,
                        ],
                      };
                      setShapes(newShapes);
                    }}
                  />
                </Group>
              );
            } else {
              return (
                <Group key={i}>
                  {/* Visual line */}
                  <Line
                    points={shape.points}
                    stroke="black"
                    strokeWidth={1}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={
                      isDraggable ? "source-over" : "destination-over"
                    }
                    // Line itself is not directly draggable.
                    draggable={false}
                  />
                  {/* Draggable transparent overlay to move the whole line */}
                  <Line
                    points={shape.points}
                    stroke="black"
                    strokeWidth={10}
                    opacity={0}
                    draggable={isDraggable}
                    onDragStart={(e) => {
                      groupDragInfo.current[i] = {
                        initialPoints: shape.points.slice(),
                        initialPos: { x: e.target.x(), y: e.target.y() },
                      };
                    }}
                    onDragMove={(e) => {
                      const info = groupDragInfo.current[i];
                      if (info) {
                        const deltaX = e.target.x() - info.initialPos.x;
                        const deltaY = e.target.y() - info.initialPos.y;
                        const newPoints = [
                          info.initialPoints[0] + deltaX,
                          info.initialPoints[1] + deltaY,
                          info.initialPoints[2] + deltaX,
                          info.initialPoints[3] + deltaY,
                        ];
                        const newShapes = [...shapes];
                        newShapes[i] = { ...newShapes[i], points: newPoints };
                        setShapes(newShapes);
                      }
                    }}
                    onDragEnd={(e) => {
                      // Reset overlay's position after dragging.
                      e.target.position({ x: 0, y: 0 });
                    }}
                  />
                  {/* Start endpoint (smaller circle) */}
                  <Circle
                    x={shape.points[0]}
                    y={shape.points[1]}
                    radius={3}
                    fill="black"
                    draggable={isDraggable}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newShapes = [...shapes];
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          pos.x,
                          pos.y,
                          newShapes[i].points[2],
                          newShapes[i].points[3],
                        ],
                      };
                      setShapes(newShapes);
                    }}
                  />
                  {/* End endpoint (smaller circle) */}
                  <Circle
                    x={shape.points[2]}
                    y={shape.points[3]}
                    radius={3}
                    fill="black"
                    draggable={isDraggable}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newShapes = [...shapes];
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          newShapes[i].points[0],
                          newShapes[i].points[1],
                          pos.x,
                          pos.y,
                        ],
                      };
                      setShapes(newShapes);
                    }}
                  />
                </Group>
              );
            }
          })}
        </Layer>
      </Stage>
    </>
  );
};

export default AnnotationStage;
